import React, { useEffect, useState, useContext } from 'react';
import { Table, Button, Modal, Form, Input, message, Popconfirm, Tag, Tabs, TreeSelect, Space, List, Avatar, Select, Popover } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined, UserOutlined, MailOutlined, PhoneOutlined, UsergroupAddOutlined, ContactsOutlined } from '@ant-design/icons';
import { GetEmpresas } from '../../../dao/metadata/GetEmpresas';
import { PostEmpresa, PutEmpresa, DeleteEmpresa } from '../../../dao/metadata/ManageEmpresas';
import { GetCursos } from '../../../dao/metadata/ManageCursos';
import { GetEmpresaContactes, PostEmpresaContacte, DeleteEmpresaContacte } from '../../../dao/metadata/ManageEmpresaContactes';
import { GetAlumnesMetadata } from '../../../dao/metadata/GetAlumnesMetadata';
import { PostAssignacio } from '../../../dao/metadata/ManageAssignacions';
import { AuthContext } from '../../../config/AuthContext';

const { TabPane } = Tabs;

const Empresas = () => {
    const { userId } = useContext(AuthContext);
    const [empresas, setEmpresas] = useState<any[]>([]);
    const [cursosTree, setCursosTree] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingEmpresa, setEditingEmpresa] = useState<any>(null);

    // Contactes state
    const [contactes, setContactes] = useState<any[]>([]);
    const [loadingContactes, setLoadingContactes] = useState(false);
    const [newContactForm] = Form.useForm();

    const [form] = Form.useForm();

    const [filters, setFilters] = useState({ nom: '', idCurs: 0, poblacio: '' });

    // Assignation State
    const [isAssignModalVisible, setIsAssignModalVisible] = useState(false);
    const [selectedEmpresaForAssign, setSelectedEmpresaForAssign] = useState<any>(null);
    const [studentsList, setStudentsList] = useState<any[]>([]);
    const [selectedStudentId, setSelectedStudentId] = useState<string>('');

    const fetchEmpresas = async () => {
        setLoading(true);
        const data = await GetEmpresas(filters.nom, filters.idCurs, filters.poblacio);
        setEmpresas(data || []);
        setLoading(false);
    };

    const fetchCursos = async () => {
        const data = await GetCursos();
        setCursosTree(buildTree(data));
    };

    const fetchContactes = async (id: number) => {
        setLoadingContactes(true);
        const data = await GetEmpresaContactes(id);
        setContactes(data || []);
        setLoadingContactes(false);
    };

    const buildTree = (flatList: any[]) => {
        const roots: any[] = [];
        const map: any = {};
        flatList.forEach(item => {
            map[item.id] = { ...item, key: item.id, value: item.id, title: item.label, children: [] };
        });
        flatList.forEach(item => {
            if (item.parent_id) {
                if (map[item.parent_id]) {
                    map[item.parent_id].children.push(map[item.id]);
                }
            } else {
                roots.push(map[item.id]);
            }
        });
        return roots;
    };

    useEffect(() => {
        fetchEmpresas();
        fetchCursos();
    }, []);

    const handleAdd = () => {
        setEditingEmpresa(null);
        setContactes([]);
        form.resetFields();
        setIsModalVisible(true);
    };

    const handleEdit = (record: any) => {
        setEditingEmpresa(record);
        form.setFieldsValue(record);
        fetchContactes(record.id);
        setIsModalVisible(true);
    };

    const handleDelete = async (id: number) => {
        await DeleteEmpresa(id);
        message.success('Empresa eliminada');
        fetchEmpresas();
    };

    const handleOk = async () => {
        try {
            const values = await form.validateFields();
            if (editingEmpresa) {
                await PutEmpresa({ ...editingEmpresa, ...values });
                message.success('Empresa actualitzada');
            } else {
                await PostEmpresa(values);
                message.success('Empresa creada');
            }
            setIsModalVisible(false);
            fetchEmpresas();
        } catch (error) {
            console.error(error);
        }
    };

    const handleAddContact = async () => {
        try {
            const values = await newContactForm.validateFields();
            if (editingEmpresa) {
                const success = await PostEmpresaContacte(
                    editingEmpresa.id, 
                    values.nom, 
                    values.email || '', 
                    values.telefon || '', 
                    values.carrec || ''
                );
                if (success) {
                    message.success('Contacte afegit');
                    newContactForm.resetFields();
                    fetchContactes(editingEmpresa.id);
                } else {
                    message.error('Error al afegir contacte');
                }
            }
        } catch (error) {
            console.error(error);
        }
    };

    const handleDeleteContact = async (id: number) => {
        const success = await DeleteEmpresaContacte(id);
        if (success) {
            message.success('Contacte eliminat');
            if (editingEmpresa) fetchContactes(editingEmpresa.id);
        } else {
            message.error('Error al eliminar contacte');
        }
    };

    const handleOpenAssign = async (record: any) => {
        setSelectedEmpresaForAssign(record);
        setIsAssignModalVisible(true);
        // Fetch all students to choose from. Pass "0" for all.
        const students = await GetAlumnesMetadata("0", undefined, undefined, undefined, userId);
        setStudentsList(students || []);
    };

    const handleAssignStudent = async () => {
        if (!selectedEmpresaForAssign || !selectedStudentId) return;

        try {
            const success = await PostAssignacio({ id_alumne: parseInt(selectedStudentId), id_empresa: selectedEmpresaForAssign.id, estat: 1 });
            if (success) {
                message.success('Alumne assignat correctament');
                setIsAssignModalVisible(false);
                setSelectedStudentId('');
            } else {
                message.error('Error al assignar alumne');
            }
        } catch (e) {
            console.error(e);
            message.error('Error al assignar alumne');
        }
    };

    const columns = [
        { title: 'Nom', dataIndex: 'nom', key: 'nom', render: (text: string) => <b>{text}</b> },
        { title: 'CIF', dataIndex: 'cif', key: 'cif' },
        { title: 'Curs', dataIndex: 'curs_label', key: 'curs_label', render: (text: string) => text ? <Tag color="blue">{text}</Tag> : '-' },
        { title: 'Població', dataIndex: 'poblacio', key: 'poblacio' },
        { title: 'Estat', dataIndex: 'estat_empresa', key: 'estat_empresa' },
        { title: 'Comentaris', dataIndex: 'comentaris', key: 'comentaris', ellipsis: true },
        { title: 'Adreça', dataIndex: 'adreca', key: 'adreca' },
        {
            title: 'Contactes',
            key: 'contactes_list',
            render: (_: any, r: any) => {
                if (!r.contactes_json) return null;
                let list = [];
                try {
                    list = typeof r.contactes_json === 'string' ? JSON.parse(r.contactes_json) : r.contactes_json;
                } catch (e) {
                    console.error("Error parsing contacts", e);
                }
                
                if (!list || list.length === 0) return null;
                
                return (
                    <Popover
                        title="Contactes de l'Empresa"
                        content={
                            <List
                                size="small"
                                dataSource={list}
                                renderItem={(c: any) => (
                                    <List.Item style={{ padding: '4px 0' }}>
                                        <div style={{ fontSize: '12px' }}>
                                            <div style={{ fontWeight: 'bold' }}>{c.nom} {c.carrec && <span style={{ fontWeight: 'normal', color: '#888' }}>({c.carrec})</span>}</div>
                                            <div style={{ color: '#666' }}>{c.email} {c.telefon && `| ${c.telefon}`}</div>
                                        </div>
                                    </List.Item>
                                )}
                            />
                        }
                        trigger="click"
                    >
                        <Button 
                            size="small" 
                            icon={<ContactsOutlined />} 
                            type="text"
                        >
                            {list.length} {list.length === 1 ? 'Contacte' : 'Contactes'}
                        </Button>
                    </Popover>
                );
            }
        },
        {
            title: 'Accions',
            key: 'action',
            width: 250,
            render: (_: any, record: any) => (
                <>
                    <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} style={{ marginRight: 8 }} />
                    <Button icon={<UsergroupAddOutlined />} onClick={() => handleOpenAssign(record)} style={{ marginRight: 8 }} title="Assignar Alumne" />
                    <Popconfirm title="Segur que vols eliminar?" onConfirm={() => handleDelete(record.id)}>
                        <Button icon={<DeleteOutlined />} danger />
                    </Popconfirm>
                </>
            ),
        },
    ];

    return (
        <div>
            {/* Filters */}
            <div style={{ marginBottom: 16, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <Input
                    placeholder="Buscar per Nom, Email o Telèfon"
                    value={filters.nom}
                    onChange={e => setFilters({ ...filters, nom: e.target.value })}
                    style={{ width: 250 }}
                    prefix={<UserOutlined />}
                    allowClear
                />
                <TreeSelect
                    treeData={cursosTree}
                    placeholder="Filtrar per Curs"
                    style={{ width: 250 }}
                    allowClear
                    treeDefaultExpandAll
                    value={filters.idCurs || undefined}
                    onChange={value => setFilters({ ...filters, idCurs: value })}
                />
                <Input
                    placeholder="Població"
                    value={filters.poblacio}
                    onChange={e => setFilters({ ...filters, poblacio: e.target.value })}
                    style={{ width: 200 }}
                    allowClear
                />
                <Button type="primary" onClick={fetchEmpresas} icon={<PlusOutlined />}>Buscar</Button>
                <Button onClick={() => setFilters({ nom: '', idCurs: 0, poblacio: '' })}>Netejar</Button>

                <div style={{ flex: 1, textAlign: 'right' }}>
                    <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                        Nova Empresa
                    </Button>
                </div>
            </div>

            <Table
                columns={columns}
                dataSource={empresas}
                rowKey="id"
                loading={loading}
            />

            <Modal
                title={editingEmpresa ? "Editar Empresa" : "Nova Empresa"}
                open={isModalVisible}
                onOk={handleOk}
                onCancel={() => setIsModalVisible(false)}
                width={700}
                footer={[
                    <Button key="back" onClick={() => setIsModalVisible(false)}>
                        Tancar
                    </Button>,
                    <Button key="submit" type="primary" onClick={handleOk}>
                        Desar Empresa
                    </Button>,
                ]}
            >
                <Tabs defaultActiveKey="1">
                    <TabPane tab="Dades Generals" key="1">
                        <Form form={form} layout="vertical">
                            <Form.Item name="nom" label="Nom" rules={[{ required: true }]}>
                                <Input />
                            </Form.Item>
                            <Form.Item name="cif" label="CIF">
                                <Input />
                            </Form.Item>
                            <Form.Item name="adreca" label="Adreça">
                                <Input />
                            </Form.Item>
                            <Form.Item name="poblacio" label="Població">
                                <Input />
                            </Form.Item>
                            <Form.Item name="id_curs" label="Curs / Cicle">
                                <TreeSelect
                                    treeData={cursosTree}
                                    placeholder="Selecciona el curs/cicle"
                                    treeDefaultExpandAll
                                />
                            </Form.Item>
                            <Form.Item name="descripcio" label="Descripció">
                                <Input.TextArea />
                            </Form.Item>
                            <Form.Item name="estat_empresa" label="Estat">
                                <Select allowClear>
                                    <Select.Option value="Proceso de selección">Proceso de selección</Select.Option>
                                    <Select.Option value="Pròximo año">Pròximo año</Select.Option>
                                    <Select.Option value="Más adelante">Más adelante</Select.Option>
                                    <Select.Option value="No buscan">No buscan</Select.Option>
                                    <Select.Option value="Imposible contactar">Imposible contactar</Select.Option>
                                </Select>
                            </Form.Item>
                            <Form.Item name="comentaris" label="Comentaris Interns">
                                <Input.TextArea rows={4} />
                            </Form.Item>
                        </Form>
                    </TabPane>
                    <TabPane tab="Contactes" key="2" disabled={!editingEmpresa}>
                        {editingEmpresa ? (
                            <>
                                <div style={{ marginBottom: 16, backgroundColor: '#f9f9f9', padding: 12, borderRadius: 8 }}>
                                    <Form form={newContactForm} layout="inline" onFinish={handleAddContact}>
                                        <Form.Item name="nom" rules={[{ required: true, message: 'Nom requerit' }]}>
                                            <Input placeholder="Nom" prefix={<UserOutlined />} />
                                        </Form.Item>
                                        <Form.Item name="email">
                                            <Input placeholder="Email" prefix={<MailOutlined />} />
                                        </Form.Item>
                                        <Form.Item name="telefon">
                                            <Input placeholder="Telèfon" prefix={<PhoneOutlined />} />
                                        </Form.Item>
                                        <Form.Item name="carrec">
                                            <Input placeholder="Càrrec" />
                                        </Form.Item>
                                        <Form.Item>
                                            <Button type="dashed" htmlType="submit" icon={<PlusOutlined />}>Afegir</Button>
                                        </Form.Item>
                                    </Form>
                                </div>
                                <List
                                    loading={loadingContactes}
                                    dataSource={contactes}
                                    renderItem={(item: any) => (
                                        <List.Item
                                            actions={[<a key="del" onClick={() => handleDeleteContact(item.id)} style={{ color: 'red' }}>Eliminar</a>]}
                                        >
                                            <List.Item.Meta
                                                avatar={<Avatar icon={<UserOutlined />} />}
                                                title={`${item.nom} (${item.carrec || 'N/A'})`}
                                                description={
                                                    <Space>
                                                        <span><MailOutlined /> {item.email}</span>
                                                        <span><PhoneOutlined /> {item.telefon}</span>
                                                    </Space>
                                                }
                                            />
                                        </List.Item>
                                    )}
                                />
                            </>
                        ) : (
                            <p>Desa l'empresa primer per poder afegir contactes.</p>
                        )}
                    </TabPane>
                </Tabs>
            </Modal>

            <Modal
                title={`Assignar Alumne a ${selectedEmpresaForAssign?.nom || ''}`}
                open={isAssignModalVisible}
                onOk={handleAssignStudent}
                onCancel={() => setIsAssignModalVisible(false)}
            >
                <p>Selecciona un alumne per assignar:</p>
                <Select
                    showSearch
                    style={{ width: '100%' }}
                    placeholder="Buscar alumne..."
                    onChange={(value: string) => setSelectedStudentId(value)}
                    value={selectedStudentId || undefined}
                    filterOption={(input, option) =>
                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    options={studentsList.map(s => ({
                        value: s.id.toString(),
                        label: `${s.nom} ${s.cognoms} (${s.email})`
                    }))}
                />
            </Modal>
        </div>
    );
};

export default Empresas;
